import React from 'react';
import { useTranslation } from 'react-i18next';
import { WeightMilestoneService } from './WeightMilestoneService';
import { WeightMilestoneDashboard } from '../models/weight-milestones';
import { extractApiErrorMessage, notifications } from '../utils';

interface UseWeightMilestonesResult {
  dashboard: WeightMilestoneDashboard | null;
  isBusy: boolean;
  /** True once a fetch has settled, successfully or not - lets callers tell "empty" from "not asked yet". */
  loaded: boolean;
  reload: () => void;
}

/**
 * Loads the dashboard's weight milestone chart.
 *
 * `farmId` picks the source: omitted, the signed-in business unit; set, another farm, which is how the
 * admin farms overview opens a farm without switching the session onto it.
 */
export const useWeightMilestones = (weeks: number, farmId?: string): UseWeightMilestonesResult => {
  const { t } = useTranslation();
  const [dashboard, setDashboard] = React.useState<WeightMilestoneDashboard | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    const service = new WeightMilestoneService();
    // The period can change while a request is in flight; ignore anything but the newest one.
    let cancelled = false;

    setIsBusy(true);

    service
      .getDashboard(weeks, farmId)
      .then((result) => {
        if (cancelled) return;
        setDashboard(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setDashboard(null);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsBusy(false);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [weeks, farmId, reloadToken]);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  return { dashboard, isBusy, loaded, reload };
};

export default useWeightMilestones;
